<template>
    <div>
        <div class="flex gap-[12px] pl-1">
            <initial-input v-for="initial in letters"
                :key="initial.index"

                ref="initials"

                :label="initial.label"
                :placeholder="initial.placeholder"
                :value="initial.value"

                @input="updateInitial(initial, $event)"
                @back="goBack(initial)"

                @focus="openKeyboard"
                @blur="closeKeyboard"
            />
        </div>
        <div
            v-if="!valid"
            class="my-[8px] text-red"
        >
            <span v-if="requireMessage"
                class="mt-[8px] flex text-sm text-gray-dark"
            >
                {{ requireMessage }}
            </span>
            <span v-else-if="justAdded">{{ $t('VALIDATION_REVIEW_JUST_ADDED') }}</span>
            <span v-else>{{ $t('VALIDATION_REVIEW') }}</span>
        </div>
        <div v-if="valid && requireMessage"
            class="mt-[8px] flex text-sm text-gray-dark"
        >
            {{ requireMessage }}
        </div>
    </div>
</template>

<script>
    import InitialInput from '../UI/inputs/InitialInput.vue';

    import allStores from '../../stores/all';

    export default {
        mixins: [allStores],

        components: {
            InitialInput,
        },

        props: {
            value: String,
            controller: Object,
            selectedOption: Object,
            valid: Boolean,
            justAdded: Boolean,
        },

        emits: ['input', 'error'],

        data() {
            return {
                letters: [],
                lastAccepted: null,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            minCharacters() {
                return +(this.selectedOption?.custom?.['personalization-limit-min'] ?? '3');
            },

            maxCharacters() {
                return +(this.selectedOption?.custom?.['personalization-limit-max'] ?? '3');
            },

            requireMessage() {
                const text = this.getText();

                if (text.length < this.minCharacters) {
                    if (this.minCharacters < 2) {
                        return this.$t('CHARACTER_IS_MANDATORY', { min: this.minCharacters });
                    }

                    return this.$t('CHARACTERS_ARE_MANDATORY', { min: this.minCharacters });
                }

                return null;
            },

            isMobile() {
                return this.appStore.isMobile;
            },
        },

        watch: {
            value() {
                if (this.value !== this.lastAccepted) {
                    this.parseText(this.value);
                    this.lastAccepted = this.value;

                    this.selectMonogram();
                }
            },

            maxCharacters(value) {
                if (value === 2) {
                    const text = this.getText();
                    // Chop off the middle letter if needed.
                    if (text.length > 2) {
                        this.parsetext(text[0] + text[2]);
                    }
                }
            },
        },

        mounted() {
            this.parseText(this.value);
            this.lastAccepted = this.value;

            this.selectMonogram();
        },

        methods: {
            updateInitial(initial, input) {
                initial.value = input;

                if (input.length > 0) {
                    this.selectMonogram(initial);
                } else {
                    this.selectMonogram();
                }
            },

            goBack(initial) {
                const prev = this.letters[initial.index - 1];
                if (prev) {
                    const prevControl = this.$refs.initials[prev.index];
                    if (prevControl) {
                        prevControl.focus();
                    }
                }
            },

            async selectMonogram(initial) {
                const monogram = this.getText();

                if (monogram.length < this.minCharacters) {
                    this.$emit('error');
                    this.advance(initial);
                    return;
                }

                // Which placement?
                const placement = this.controller.findComponentPlacement(this.selectedOption.code);

                const updateResult = await this.viewController.updatePersonalization(placement.code, monogram, {
                    preview: true,
                });

                if (updateResult.accepted) {
                    this.lastAccepted = monogram;

                    this.$emit('input', {
                        controller: this.controller,
                        placement,
                        text: monogram,
                    });

                    // Advance if accepted.
                    this.advance(initial);
                } else if (initial) {
                    initial.value = '';
                }
            },

            parseText(text) {
                const labels = [
                    this.$t('FIRST_INITIAL'),
                    this.$t('MIDDLE_INITIAL'),
                    this.$t('LAST_INITIAL'),
                ];

                const placeholders = ['A', 'B', 'C'];

                this.letters = [];

                for (let i = 0; i < this.maxCharacters; i += 1) {
                    this.letters.push({
                        index: i,
                        value: text[i] ?? '',
                        label: labels[i] ?? '',
                        placeholder: placeholders[i],
                    });
                }
            },

            advance(initial) {
                if (initial) {
                    const next = this.letters[initial.index + 1];
                    if (next) {
                        const nextControl = this.$refs.initials[next.index];
                        if (nextControl) {
                            nextControl.focus();
                        }
                    }
                }
            },

            getText() {
                return this.letters.map((x) => x.value).join('');
            },

            openKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(true);
                }
            },

            closeKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(false);
                }
            },
        },
    };
</script>
